"use strict";

exports.__esModule = true;
exports.InteractiveCreative = void 0;
var _StringUtils = require("../Utils/StringUtils");
var _TrackingReport = require("./TrackingReport");
var _CreativeEventHandler2 = require("../Public/CreativeEventHandler");
var _NonLinearCreative = require("./NonLinearCreative");
var _VASTProperty = require("./VASTProperty");
var _YoLog = require("../Utils/YoLog");
var _HttpUtils = require("../Utils/HttpUtils");
function _createForOfIteratorHelperLoose(o, allowArrayLike) { var it = typeof Symbol !== "undefined" && o[Symbol.iterator] || o["@@iterator"]; if (it) return (it = it.call(o)).next.bind(it); if (Array.isArray(o) || (it = _unsupportedIterableToArray(o)) || allowArrayLike && o && typeof o.length === "number") { if (it) o = it; var i = 0; return function () { if (i >= o.length) return { done: true }; return { done: false, value: o[i++] }; }; } throw new TypeError("Invalid attempt to iterate non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }
function _unsupportedIterableToArray(o, minLen) { if (!o) return; if (typeof o === "string") return _arrayLikeToArray(o, minLen); var n = Object.prototype.toString.call(o).slice(8, -1); if (n === "Object" && o.constructor) n = o.constructor.name; if (n === "Map" || n === "Set") return Array.from(o); if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen); }
function _arrayLikeToArray(arr, len) { if (len == null || len > arr.length) len = arr.length; for (var i = 0, arr2 = new Array(len); i < len; i++) arr2[i] = arr[i]; return arr2; }
function _inheritsLoose(subClass, superClass) { subClass.prototype = Object.create(superClass.prototype); subClass.prototype.constructor = subClass; _setPrototypeOf(subClass, superClass); }
function _setPrototypeOf(o, p) { _setPrototypeOf = Object.setPrototypeOf ? Object.setPrototypeOf.bind() : function _setPrototypeOf(o, p) { o.__proto__ = p; return o; }; return _setPrototypeOf(o, p); }
/**
 * Represents an interactive creative object.
 *
 * Encapsulates the data from the `<InteractiveCreativeFile>` element of a VAST document.<br/>
 * It is used to provide a more interactive experience using additional creative functionality by making
 * use of a vendor's interactive APIs.
 *
 * <b>NOTE:</b> The class implements CreativeEventHandler so that it can raise analytic events on behalf
 * of a client application. It supports firing of customer tracker URLs for Interactive creative of type SIMID.
 * 
 *  @hideconstructor
 * @see {@link CreativeEventHandler}, which this class implements
 */
var InteractiveCreative = /*#__PURE__*/function (_CreativeEventHandler) {
  _inheritsLoose(InteractiveCreative, _CreativeEventHandler);
  function InteractiveCreative(source, properties, broker, tracking) {
    var _this;
    _this = _CreativeEventHandler.call(this) || this;

    /** @private */
    _this.source = source;
    /** @private */
    _this.properties = properties ? properties : [];
    /** @private */
    _this.broker = broker;
    /** @private */
    _this.adParameters = null;
    /** @private */
    _this.advertDuration = 0;
    /** @private */
    _this.tracking = null;
    /** @private */
    _this.offset = 0;
    /** @private */
    _this.visible = false;
    /** @private */
    _this.nonLinearCreatives = [];
    if (_this.isSIMID()) {
      _this.tracking = tracking;
    }
    return _this;
  }

  /**
   * @return {string} the ad parameters for the interactive creative, which may be null.
   */
  var _proto = InteractiveCreative.prototype;
  _proto.getAdParameters = function getAdParameters() {
    return this.adParameters;
  }

  /**
   * @return {number} the duration of the advert media for the interactive creative, in milliseconds.
   */;
  _proto.getAdvertDuration = function getAdvertDuration() {
    return this.advertDuration;
  }

  /**
   * 
   * @returns {NonLinearCreative[]} an array of {@link NonLinearCreative} objects associated with the interactive creative.
   *                                A {@link NonLinearCreative} is related if its `apiFramework` property
   *                                is the same as the interactive creative.
   */;
  _proto.getNonLinearCreatives = function getNonLinearCreatives() {
    return this.nonLinearCreatives;
  }

  /**
   * Returns the properties for the interactive creative. The properties are represented as {VASTProperty} objects.
   * 
   * Possible properties are:
   * - type
   * - apiFramework
   * - variableDuration
   * 
   * @return {VASTProperty[]} an array of {@link VASTProperty} objects for the interactive creative
   */;
  _proto.getProperties = function getProperties() {
    return Object.assign([], this.properties);
  }

  /**
   * Returns the VAST property with the given name
   * 
   * Possible properties are:
   * - type
   * - apiFramework
   * - variableDuration
   * 
   * @param {string} name the name of the property
   * @return {VASTProperty} a {@link VASTProperty} object or null if not found
   */;
  _proto.getProperty = function getProperty(name) {
    for (var _iterator = _createForOfIteratorHelperLoose(this.properties), _step; !(_step = _iterator()).done;) {
      var prop = _step.value;
      if (prop.getName() === name) {
        return prop;
      }
    }
    return null;
  }

  /**
   * @return {string} the source URL for the interactive creative
   */;
  _proto.getSource = function getSource() {
    return this.source;
  };
  _proto.getTracking = function getTracking() {
    return this.tracking;
  };
  _proto.initialise = function initialise(adParameters, advertDuration, nonlinears) {
    this.adParameters = adParameters;
    this.advertDuration = advertDuration;
    this.nonLinearCreatives = [];
    var prop = this.getProperty("apiFramework");
    if (prop) {
      var propValue = prop.getValue();
      for (var _iterator2 = _createForOfIteratorHelperLoose(nonlinears), _step2; !(_step2 = _iterator2()).done;) {
        var nlc = _step2.value;
        var nlcProp = nlc.getProperty("apiFramework");
        if (nlcProp && nlcProp.getValue() === propValue) {
          this.nonLinearCreatives.push(nlc);
        }
      }
    }
  };
  _proto.isSIMID = function isSIMID() {
    for (var _iterator3 = _createForOfIteratorHelperLoose(this.properties), _step3; !(_step3 = _iterator3()).done;) {
      var prop = _step3.value;
      if (prop.getName() === "apiFramework" && prop.getValue() === "SIMID") {
        return true;
      }
    }
    return false;
  };
  _proto.isValid = function isValid() {
    return !(0, _StringUtils.isNullOrEmpty)(this.source);
  }

  /**
   * @return {boolean} true if the interactive creative is active and is to be displayed. Tracking events are fired only when the interactive creative is visible.
   */;
  _proto.isVisible = function isVisible() {
    return this.visible;
  };
  _proto.onClickThrough = function onClickThrough() {
    // No op
  };
  _proto.onTrackingEvent = function onTrackingEvent(url) {
    if (!this.isVisible()) {
      _YoLog.YoLog.w("Warning: InteractiveCreative is not visible");
      return;
    }
    if (!(0, _HttpUtils.getUrl)(url)) {
      _YoLog.YoLog.w("Invalid custom tracker URL");
      return;
    }

    // custom trackers are fired at the discretion of the client application,
    // but only for a SIMID implementation
    if (this.isSIMID()) {
      this.broker.fireTrackingReport(new _TrackingReport.TrackingReport("custom", [url]));
    }
  }

  // for test purposes
  ;
  _proto.setTracking = function setTracking(report) {
    this.tracking = report;
  }

  /**
   * Defines whether the Interactive Creative is being presented to the user.
   * @param {boolean} visible the visibility flag
   */;
  _proto.setVisible = function setVisible(visible) {
    if (this.visible !== visible) {
      this.visible = visible;
      if (this.visible && this.isSIMID() && this.tracking) {
        // fire SIMID beacons
        this.broker.fireTrackingReport(this.tracking);
      }
    }
  };
  _proto.toString = function toString() {
    var ret = "InteractiveCreative source: " + this.source;
    if (this.properties.length > 0) {
      ret += "\nProperties:";
      for (var _iterator4 = _createForOfIteratorHelperLoose(this.properties), _step4; !(_step4 = _iterator4()).done;) {
        var prop = _step4.value;
        ret += (0, _StringUtils.toIndentedString)(prop);
      }
    }
    if (this.tracking) {
      ret += "\nTracking event:";
      ret += (0, _StringUtils.toIndentedString)("\n " + this.tracking);
    }
    return ret;
  };
  return InteractiveCreative;
}(_CreativeEventHandler2.CreativeEventHandler);
exports.InteractiveCreative = InteractiveCreative;